/**
 * `/$tenantSlug/settings/rbac` — Roles & permissions (read-only view).
 *
 * Per KEN_LAYER_7 §7.7 + ADR-036 + named_roles (ADR-0097 / ADR-0099).
 *
 * Reads the tenant's roles via GET /api/shell/rbac/roles and renders one
 * READ-ONLY card per role. System roles (`is_system`) are fixed bundles of
 * permissions seeded per tenant (owner / admin / bookkeeper / sales /
 * warehouse / read_only + advisor) — they can no longer be edited here
 * (ADR-0099 added an `is_system` write-guard that raises 42501). Each card
 * shows the role's display label, a "System role" text chip, its description,
 * a plain-English permission summary, and a note that customizing via clone is
 * coming soon. Custom (`is_system = false`) roles render the same read-only
 * card with a permission COUNT in place of the curated summary.
 *
 * The editable checkbox grid + Save + the dirty-state machinery + the module
 * permission catalogue import were removed: nothing on this page mutates a
 * system role any more, and the clone-to-customize builder is a later slice.
 *
 * Per-user overrides remain a separate sub-page (the override endpoints are
 * reachable via the API + the overrides link).
 */

import { RequirePermission } from "@ken/auth";
import { Card, CardContent, CardHeader, CardTitle, Chip, Skeleton } from "@ken/ui";
import { createFileRoute, Link, Outlet, useLocation, useParams } from "@tanstack/react-router";
import { useEffect, useState, type ReactElement } from "react";

import { roleLabel, roleSummary } from "../lib/roles";

export const Route = createFileRoute("/$tenantSlug/settings/rbac")({
  component: RbacSettingsRoute,
});

/**
 * Hard permission gate (defense-in-depth): the role catalogue is an admin
 * surface and must require `shell.rbac.view`. RequirePermission renders an
 * accessible "no access" region instead. ADR-0090 (advisor envelope) / ADR-036.
 */
function RbacSettingsRoute(): ReactElement {
  return (
    <RequirePermission slug="shell.rbac.view">
      <RbacSettingsContent />
    </RequirePermission>
  );
}

interface Role {
  id: string;
  role_name: string;
  description: string | null;
  is_system: boolean;
  permission_ids: string[];
}

/**
 * The plain-English permission summary for a role card. System roles use the
 * curated one-liner (roleSummary); any custom role (or a system role with no
 * curated summary) falls back to a permission count.
 */
function permissionSummary(role: Role): string {
  const summary = roleSummary(role.role_name);
  if (role.is_system && summary !== null) return summary;
  const n = role.permission_ids.length;
  return `${n} permission${n === 1 ? "" : "s"}.`;
}

function RbacSettingsContent(): ReactElement {
  const { tenantSlug } = useParams({ from: "/$tenantSlug" });
  const location = useLocation();
  // Sub-routes (overrides) render through the Outlet. Show the role
  // catalogue only at the exact /settings/rbac URL.
  const onRbacRoot =
    location.pathname.endsWith("/settings/rbac") ||
    location.pathname.endsWith("/settings/rbac/");

  const [roles, setRoles] = useState<Role[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch("/api/shell/rbac/roles", { credentials: "include" });
        if (cancelled) return;
        if (res.status === 401) {
          setError("Your session expired.");
          return;
        }
        if (res.status === 403) {
          setError("You don't have permission to view roles.");
          return;
        }
        if (!res.ok) {
          setError(`Couldn't load roles (status ${res.status}).`);
          return;
        }
        const body = (await res.json()) as Role[];
        if (cancelled) return;
        setRoles(body);
      } catch (err) {
        if (!cancelled) setError(err instanceof Error ? err.message : "Network error.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  if (!onRbacRoot) {
    return <Outlet />;
  }

  return (
    <div className="flex flex-col gap-6" data-testid="shell-settings-rbac-page">
      <header className="flex items-end justify-between gap-4">
        <div>
          <div className="text-[12px] uppercase tracking-[0.08em] text-[var(--color-text-faint)] font-semibold">
            Tenant · Settings
          </div>
          <h1 className="text-[26px] font-bold tracking-tight">Roles &amp; permissions</h1>
          <p className="text-sm text-[var(--color-text-soft)] mt-1">
            System roles are fixed bundles of permissions. Cloning to customize
            is coming soon.
          </p>
        </div>
        <Link
          to="/$tenantSlug/settings/rbac/overrides"
          params={{ tenantSlug }}
          data-testid="shell-settings-rbac-overrides-link"
          className="text-sm text-[var(--color-accent-blue)] underline-offset-2 hover:underline"
        >
          Per-user overrides →
        </Link>
      </header>

      {error !== null && (
        <div
          role="alert"
          data-testid="shell-settings-rbac-error"
          className="text-sm text-[var(--color-accent-red)] px-3 py-2 rounded-[var(--radius-sm)] bg-[oklch(99%_0.04_25_/_0.4)] border border-[var(--color-accent-red)]"
        >
          {error}
        </div>
      )}

      {loading ? (
        <div data-testid="shell-settings-rbac-loading" className="flex flex-col gap-3">
          <Skeleton className="h-40 w-full rounded-[var(--radius-md)]" />
          <Skeleton className="h-40 w-full rounded-[var(--radius-md)]" />
        </div>
      ) : roles.length === 0 ? (
        <Card surface="glass-strong" padding="lg">
          <CardContent>
            <div
              data-testid="shell-settings-rbac-empty"
              className="text-sm text-[var(--color-text-soft)] text-center py-8"
            >
              No roles defined yet for this tenant.
            </div>
          </CardContent>
        </Card>
      ) : (
        <section
          aria-labelledby="rbac-roles-heading"
          className="flex flex-col gap-4"
        >
          {/* Section heading keeps the heading order h1 → h2 → (card) h3
              valid for assistive tech (WCAG heading-order). */}
          <h2 id="rbac-roles-heading" className="sr-only">
            Roles
          </h2>
          {roles.map((role) => (
            <Card
              key={role.id}
              surface="glass-strong"
              data-testid={`shell-settings-rbac-role-${role.role_name}`}
            >
              <CardHeader>
                <div className="flex items-center gap-3 min-w-0">
                  <CardTitle className="truncate">
                    {roleLabel(role.role_name)}
                  </CardTitle>
                  {/* System indicator as TEXT, never colour-only (a11y §G.7). */}
                  {role.is_system ? (
                    <Chip
                      tone="brand"
                      data-testid={`shell-settings-rbac-role-${role.role_name}-type`}
                    >
                      System role
                    </Chip>
                  ) : (
                    <Chip
                      tone="default"
                      data-testid={`shell-settings-rbac-role-${role.role_name}-type`}
                    >
                      Custom role
                    </Chip>
                  )}
                </div>
                {role.description !== null && role.description !== "" && (
                  <div className="text-sm text-[var(--color-text-soft)] mt-1">
                    {role.description}
                  </div>
                )}
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-2">
                  <div
                    data-testid={`shell-settings-rbac-role-${role.role_name}-summary`}
                    className="text-sm text-[var(--color-text-soft)]"
                  >
                    {permissionSummary(role)}
                  </div>
                  {role.is_system && (
                    <div
                      data-testid={`shell-settings-rbac-role-${role.role_name}-readonly-note`}
                      className="text-[13px] text-[var(--color-text-soft)] px-3 py-2 rounded-[var(--radius-sm)] bg-[var(--color-surface-soft)] border border-[var(--color-border-hairline)]"
                    >
                      System role — built in, can&apos;t be edited yet. Cloning
                      to customize is coming soon.
                    </div>
                  )}
                </div>
              </CardContent>
            </Card>
          ))}
        </section>
      )}
    </div>
  );
}
